/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, fmt$ } from "../shared/globals.js";

export default function PerformanceAllocationChart({ assetData, sortedData, totalValue, totalPnl, COLORS, isMobile }) {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 600 });
  const [perfHover, setPerfHover] = useState(null);

  useEffect(() => {
    const obs = new ResizeObserver(es => {
      const { width } = es[0].contentRect;
      if (width > 0) setDims({ w: width });
    });
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setDims({ w: width });
        }
      }));
    }
    return () => obs.disconnect();
  }, []);

  // ── Performance data ──────────────────────────────────────────────────────
  const perfByDate = (() => {
    const dateMap = {};
    assetData.forEach(a => {
      let cumShares = 0, cumDollars = 0;
      (a.entries || []).forEach(e => {
        cumShares += e.shares;
        cumDollars += e.dollars;
        if (!dateMap[e.date]) dateMap[e.date] = {};
        dateMap[e.date][a.id] = { shares: cumShares, dollars: cumDollars };
      });
    });
    const dates = Object.keys(dateMap).sort();
    const lastState = {};
    return dates.map(date => {
      Object.keys(dateMap[date]).forEach(id => { lastState[id] = dateMap[date][id]; });
      let tv = 0, ti = 0;
      assetData.forEach(a => {
        const s = lastState[a.id];
        if (s) { tv += s.shares * a.livePrice; ti += s.dollars; }
      });
      return { date, value: tv, invested: ti };
    });
  })();

  const PAD = { t:20, r:20, b:30, l:60 };
  const W = Math.max(dims.w - 32, 100), H = 180;
  const gW = W - PAD.l - PAD.r, gH = H - PAD.t - PAD.b;
  const maxVal = Math.max(...perfByDate.map(d => Math.max(d.value, d.invested)), 1);
  const xS = i => PAD.l + (i / Math.max(perfByDate.length - 1, 1)) * gW;
  const yS = v => PAD.t + gH - (v / maxVal) * gH;
  const buildPath = key => {
    if (perfByDate.length < 2) return "";
    let d = `M ${xS(0)} ${yS(perfByDate[0][key])}`;
    for (let i = 1; i < perfByDate.length; i++) {
      const cpx = (xS(i-1) + xS(i)) / 2;
      d += ` C ${cpx} ${yS(perfByDate[i-1][key])}, ${cpx} ${yS(perfByDate[i][key])}, ${xS(i)} ${yS(perfByDate[i][key])}`;
    }
    return d;
  };

  // ── Allocation donut ──────────────────────────────────────────────────────
  const SIZE = 160, CX = 80, CY = 80, R = 58, THICK = 16;
  const circ = 2 * Math.PI * R;
  let offset = 0;
  const sorted = [...sortedData].sort((a, b) => b.currentValue - a.currentValue);
  const arcs = sorted.map((a, i) => {
    const pct = totalValue > 0 ? a.currentValue / totalValue : 0;
    const arc = pct * circ;
    const dasharray = `${arc} ${circ}`;
    const dashoffset = -offset;
    offset += arc;
    return { ...a, dasharray, dashoffset, color: COLORS[i % COLORS.length], pct };
  });

  return (
    <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 16, alignItems:"stretch" }}>

      {/* Performance chart */}
      <div ref={wrapRef} style={{ flex:2, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"16px 16px 8px", minWidth:0 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", marginBottom:12 }}>PORTFOLIO PERFORMANCE</div>
        <div style={{ width:"100%", position:"relative" }}>
          <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ display:"block" }}>
            {[0.25,0.5,0.75,1].map(f => (
              <g key={f}>
                <line x1={PAD.l} y1={yS(maxVal*f)} x2={W-PAD.r} y2={yS(maxVal*f)} stroke="var(--border)" strokeWidth="1"/>
                <text x={PAD.l-6} y={yS(maxVal*f)+4} textAnchor="end" fill="var(--text5)" fontSize="9" fontFamily="Montserrat,sans-serif">
                  {maxVal*f >= 1e6 ? `$${(maxVal*f/1e6).toFixed(1)}M` : `$${(maxVal*f/1000).toFixed(0)}k`}
                </text>
              </g>
            ))}
            <path d={buildPath("invested")} fill="none" stroke="var(--text4)" strokeWidth="1.5" strokeDasharray="4 3"/>
            <path d={buildPath("value")} fill="none" stroke={totalPnl >= 0 ? C.green : C.red} strokeWidth="2.5" strokeLinecap="round"/>
            {/* Hover dots */}
            {perfHover !== null && (() => {
              const d = perfByDate[perfHover];
              if (!d) return null;
              const px = xS(perfHover), pyV = yS(d.value), pyI = yS(d.invested);
              return (<>
                <circle cx={px} cy={pyV} r={4} fill={totalPnl >= 0 ? C.green : C.red} stroke="var(--bg2)" strokeWidth={2}/>
                <circle cx={px} cy={pyI} r={3} fill="var(--text4)" stroke="var(--bg2)" strokeWidth={2}/>
                <line x1={px} y1={PAD.t} x2={px} y2={H-PAD.b} stroke="var(--border2)" strokeWidth={1} strokeDasharray="3 2"/>
              </>);
            })()}
            {/* Invisible overlay for hover */}
            <rect x={PAD.l} y={PAD.t} width={gW} height={gH} fill="transparent"
              onMouseMove={e => {
                const svg = e.currentTarget.closest("svg");
                const rect = svg.getBoundingClientRect();
                const mx = (e.clientX - rect.left) * (W / rect.width);
                const idx = Math.round((mx - PAD.l) / gW * (perfByDate.length - 1));
                setPerfHover(Math.max(0, Math.min(perfByDate.length - 1, idx)));
              }}
              onMouseLeave={() => setPerfHover(null)}
            />
          </svg>
          {/* Tooltip */}
          {perfHover !== null && (() => {
            const d = perfByDate[perfHover];
            if (!d) return null;
            const pnl = d.value - d.invested;
            const pnlPct = d.invested > 0 ? (pnl / d.invested) * 100 : 0;
            const col = pnl >= 0 ? C.green : C.red;
            const leftPct = perfHover / Math.max(perfByDate.length - 1, 1);
            return (
              <div style={{
                position:"absolute", pointerEvents:"none",
                left: `calc(${leftPct * gW + PAD.l}px - 60px)`,
                top: 8, zIndex:10,
                background:"var(--bg3)", border:"1px solid var(--border2)",
                borderRadius:7, padding:"7px 11px", minWidth:120,
              }}>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)", letterSpacing:"0.1em", marginBottom:4 }}>{d.date}</div>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text)" }}>VALUE <span style={{ color: totalPnl>=0?C.green:C.red }}>{fmt$(d.value)}</span></div>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text4)" }}>INVESTED {fmt$(d.invested)}</div>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:col, marginTop:2 }}>{pnl>=0?"+":""}{fmt$(pnl)} ({pnlPct>=0?"+":""}{pnlPct.toFixed(1)}%)</div>
              </div>
            );
          })()}
        </div>
        <div style={{ display:"flex", gap:16, justifyContent:"flex-end", marginTop:4, marginBottom:4 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:14, height:2.5, background: totalPnl >= 0 ? C.green : C.red, borderRadius:1 }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)" }}>VALUE</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:14, height:0, borderTop:"2px dashed var(--text4)" }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, color:"var(--text4)" }}>INVESTED</span>
          </div>
        </div>
      </div>

      {/* Allocation donut */}
      <div style={{ flex:1, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"16px 20px", minWidth:0 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", marginBottom:16 }}>ALLOCATION</div>
        <div style={{ display:"flex", alignItems:"center", gap:20 }}>
          <div style={{ position:"relative", width:SIZE, height:SIZE, flexShrink:0 }}>
            <svg width={SIZE} height={SIZE} style={{ transform:"rotate(-90deg)" }}>
              <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--border)" strokeWidth={THICK}/>
              {arcs.map(a => (
                <circle key={a.id} cx={CX} cy={CY} r={R} fill="none"
                  stroke={a.color} strokeWidth={THICK}
                  strokeDasharray={a.dasharray}
                  strokeDashoffset={a.dashoffset}
                  strokeLinecap="butt"
                />
              ))}
            </svg>
            <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:"var(--text4)", letterSpacing:"0.1em" }}>TOTAL</div>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:13, fontWeight:900, color:"var(--text)", marginTop:2 }}>{fmt$(totalValue)}</div>
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:10, flex:1, maxHeight:200, overflowY:"auto", paddingRight:4 }}>
            {arcs.map(a => (
              <div key={a.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:a.color, flexShrink:0 }}/>
                  <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:900, color:C.orange }}>{a.ticker}</span>
                </div>
                <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:12, fontWeight:700, color:a.color }}>{(a.pct*100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
